import React, { useEffect, useState } from 'react';
import {
    HeartHandshake,
    Ban,
    HelpCircle,
    History,
    Trash2,
    Award,
    Vault,
    Home,
    Crown,
    LogOut,
    UserX,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import borderImage from '../assets/images/border-top.png';
import bgHoroscope from '../assets/images/bgHoroscope.jpg';
import premiumBadge from '../assets/images/premium-badge.png';
import maleUnknown from '../assets/images/maleUnknown.jpg';
import femaleUnknown from '../assets/images/femaleUnknown.jpg';
import unknownUser from '../assets/images/UnknownUser.webp';
import { Button } from '.';
import type { RootState } from '../store';
import { capitalizeName } from '../utils/capitalize';

const menuItems = [
    {
        title: 'Home',
        icon: Home,
        path: '/home'
    },
    {
        title: 'Subscription',
        icon: Award,
        path: 'subscription'
    },
    {
        title: 'Payment History',
        icon: History,
        path: 'checkout'
    },
    {
        title: 'Active Plan',
        icon: Crown,
        path: 'active-plan'
    },
    {
        title: 'Create Horoscope',
        icon: Vault,
        path: 'create-horoscope'
    },
    {
        title: 'Success Stories',
        icon: HeartHandshake,
        path: 'success-stories'
    },
    {
        title: 'Ignored Profiles',
        icon: Ban,
        path: 'ignored-profiles',
    },
    {
        title: 'Remove Account',
        icon: UserX,
        path: 'remove-account',
    },
    {
        title: 'Help and Support',
        icon: HelpCircle,
        path: 'help'
    },
];

const SideMenu: React.FC<{
    closeMenu?: () => void
    deactivate: () => void
    logout: () => void;
}> = ({ closeMenu, deactivate, logout }) => {
   
    const userId = useSelector((state: RootState) => state.user.userId)
    const userIsPremium = useSelector((state: RootState) => state.user.is_premium)

    const userName = useSelector((state: RootState) => `${state.user.first_name} ${state.user.last_name}`)
    const userGender = useSelector((state: RootState) => state.user.gender)
    const userImages = useSelector((state: RootState) => state.user.userImages)
    const userHoroscope = useSelector((state: RootState) => state.user.userHoroscope)
    const maxProfileViewLimit = useSelector((state: RootState) => state.user.maxProfileViewLimit)
    const profileViewCount = useSelector((state: RootState) => state.user.profileViewCount)

    const navigate = useNavigate();
    const location = useLocation();
    const [activeSection, setActiveSection] = useState('Home');

    useEffect(() => {
        if (location.pathname === '/home') {
            setActiveSection('Home');
        }
    }, [location.pathname]);

    return (
        <div className='rounded-xl bg-secondary shadow-lg h-screen lg:h-full overflow-y-scroll no-scrollbar'>
            <div className='flex flex-col gap-6 py-8 font-outfit'>
                <div className='flex flex-col items-center text-textColor'>
                    <img
                        src={userImages?.[0]?.url || (userGender === 'Male' ? maleUnknown : userGender === 'Female' ? femaleUnknown : unknownUser)}
                        alt='profileImage'
                        className='h-32 w-32 mb-2 object-cover rounded-full'
                    />
                    <span className='font-medium text-lg'>{userName ? capitalizeName(userName) : userName}</span>
                    <span className='text-sm font-medium'>{userId}</span>

                    {userIsPremium && (
                        <>
                            <div className='flex items-center gap-1 mx-2 -ml-1'>
                                <img src={premiumBadge} alt='premiumBadge' className='size-8' />
                                <span className='text-goldenHover font-semibold font-outfit text-sm'>Premium Member</span>
                            </div>
                            {maxProfileViewLimit > 0 && (
                                <div className="w-48 mt-2 px-4">
                                    <div className="text-xs font-medium text-textColor mb-1">
                                        Profile Views: {profileViewCount} / {maxProfileViewLimit}
                                    </div>
                                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-primary transition-all duration-300"
                                            style={{
                                                width: `${Math.min((profileViewCount / maxProfileViewLimit) * 100, 100)}%`
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <button
                        onClick={() => {
                            navigate('profile')
                            setActiveSection('Profile');
                            closeMenu?.();
                        }}
                        className={`text-sm border-2 border-primary hover:bg-primary active:bg-darkPrimary transition-colors duration-200 rounded-full h-8 px-3 text-primary hover:text-secondary mt-3
                        ${activeSection === 'Profile' && 'bg-primary text-secondary'}`}>
                        VIEW/EDIT PROFILE
                    </button>
                </div>

                {!userIsPremium && (
                    <div className='flex flex-col gap-3 items-center'>
                        <img src={borderImage} alt="borderImage" className='px-6 lg:px-10' />
                        <div className='px-4 lg:px-6 border-2 rounded-xl border-textLight pb-2'>
                            <div className='flex flex-col gap-4 items-center my-4 lg:my-7'>
                                <h1 className='text-center text-textColor text-md lg:text-xl font-semibold'>Upgrade and Unlock</h1>
                                <ol className="text-sm lg:text-md text-textColor list-decimal list-inside space-y-2 pb-2">
                                    <li>View full contact details</li>
                                    <li>Send interest</li>
                                    <li>Accept interest</li>
                                    <li>Check horoscope matching</li>
                                </ol>
                                <Button name='UPGRADE NOW' type='first' htmlType='button'
                                    onClick={() => {
                                        navigate('subscription')
                                        setActiveSection('Subscription')
                                        closeMenu?.();
                                    }} />
                            </div>
                        </div>
                    </div>
                )}

                {!userHoroscope?.key && (
                    <div className='flex flex-col gap-3 items-center'>
                        <img src={borderImage} alt="borderImage" className='px-6 lg:px-10' />
                        <div className='mx-4 lg:mx-6 bg-no-repeat bg-cover border-2 rounded-xl border-textLight pb-2'
                            style={{ backgroundImage: `url(${bgHoroscope})` }}>
                            <div className='flex flex-col gap-4 items-center my-4 lg:my-7 px-3'>
                                <span className='text-center text-primary lg:text-xl font-medium font-yesevaOne'>Create Your Horoscope in simple steps</span>
                                <p className='text-sm text-center text:textColor font-outfit mb-2'> Upload your created horoscope to enhance your profile and check horoscope compatibility with your partner.</p>
                                <Button name='CREATE HOROSCOPE' type='first' htmlType='button' onClick={() => {
                                    navigate('create-horoscope')
                                    setActiveSection('Create Horoscope')
                                    closeMenu?.();
                                }} />
                            </div>
                        </div>
                    </div>
                )}

                <img src={borderImage} alt="borderImage" className='px-6 lg:px-10' />
                <nav className='flex-1 space-y-1 mx-auto'>
                    {menuItems
                        .filter((item) => item.title !== "Active Plan" || userIsPremium)
                        .map((item) => (
                            <a
                                key={item.path}
                                href={item.path}
                                className={`flex items-center gap-3 px-4 py-2 text-md font-outfit text-textColor hover:text-golden focus:text-primary rounded-lg transition-colors
                                ${activeSection === item.title ? 'bg-primary bg-opacity-20' : 'hover:bg-primary hover:bg-opacity-10'}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveSection(item.title);
                                    if (item.path === 'help') {
                                        window.open(`https://wa.me/+918593964196?text=Hello, I need help.`, '_blank');
                                    } else if (item.path === 'remove-account') {
                                        navigate('/remove-account');
                                    } else {
                                        navigate(item.path);
                                    }
                                    closeMenu?.();
                                }}>
                                <item.icon className="w-5 h-5 mr-3" />
                                <span>{item.title}</span>
                            </a>
                        ))}
                </nav>

                <div className='space-y-1 mx-auto mb-32 pb-10 sm:mb-24 md:mb-24 lg:mb-10 xl:mb-10'>
                    <button
                        onClick={deactivate}
                        className='flex items-center w-full gap-3 py-2 text-md font-outfit text-red-600 hover:bg-red-50 rounded-lg transition-colors'>
                        <Trash2 className="w-5 h-5" />
                        <span>Deactivate Account</span>
                    </button>
                    <button
                        onClick={logout}
                        className='flex items-center w-full gap-3 py-2 text-md font-outfit text-gray-600 hover:bg-gray-100 rounded-lg transition-colors'>
                        <LogOut className="w-5 h-5" />
                        <span>Logout</span>
                    </button>
                </div>
            </div>
        </div>
    )
}

export default SideMenu;
